import React, { useState, useEffect } from 'react';
import RecipientPicker from '../../../../components/emails/RecipientPicker';
import useBackdropClose from '../../../../hooks/useBackdropClose';

/**
 * SendEstimatIntroModal - Modal som visas när BK skickar estimat för första gången
 * Låter BK redigera ett intro-meddelande som visas högst upp i estimatet för kund
 */
const DEFAULT_TEMPLATE = `Hej {{FÖRNAMN}}{{FÖRNAMN2_PREFIX}},

Tack för ett trevligt och givande möte. Det var kul att få höra om det spännande projektet som är på gång!

Här nedan är kostnadsestimatet för projektet. Ha i åtanke att vi ännu inte spikat alla beståndsdelar, dock så är det baserat på snittpriser från många tidigare projekt och ger därför ett bra riktmärke.

Först och främst undrar jag:
• Matchar detta er önskade budget?
• Är det någon del vi ska prioritera bort/ned/upp?

Skriv feedback, frågor eller korrigeringar nedan. Föredrar ni att vi bokar ett Teams-möte för att gå igenom estimatet så kan vi göra det.

När/om estimatet ser bra ut så sätter jag igång ett mer djupgående arbete med hembesök, arbetsbeskrivning, offerter osv.

Med vänlig hälsning,
{{BKNAMN}}

_Var god respektera den för er kostnadsfria tid som lagts på detta underlag genom att inte dela det med andra aktörer utan vårt medgivande. Tack på förhand._`;

/**
 * Fyll i placeholders med faktiska värden
 */
const fillPlaceholders = ({ firstName, firstName2, bkName }) => {
  const name2Prefix = firstName2 ? ` & ${firstName2}` : '';
  return DEFAULT_TEMPLATE
    .replace('{{FÖRNAMN}}', firstName || 'kund')
    .replace('{{FÖRNAMN2_PREFIX}}', name2Prefix)
    .replace('{{BKNAMN}}', bkName || '');
};

const SendEstimatIntroModal = ({
  isOpen,
  onClose,
  onSend,
  customerFirstName,
  customerEmail = '',
  beställare2FirstName,
  beställare2Email = '',
  bkFirstName,
  isSending = false,
  versionComment = '', // Om det är en uppdatering
  hasBaspaket = true,  // Om false visas en confirm-fråga innan utskick
  // Andra parallella estimat som kan skickas i samma vända.
  // [{ _id, variantName, version, status }]
  ovrigaEstimat = [],
}) => {
  const mottagare = [
    customerEmail && { id: customerEmail, name: customerFirstName, email: customerEmail, role: 'Kund', selected: true },
    beställare2Email && { id: beställare2Email, name: beställare2FirstName, email: beställare2Email, role: 'Beställare 2', selected: true }
  ].filter(Boolean);

  const [text, setText] = useState('');
  // Vilka av de parallella estimaten som ska med. Alla förkryssade — det är
  // det vanliga fallet, och att kryssa ur är ett aktivt val.
  const [valdaExtra, setValdaExtra] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setValdaExtra((ovrigaEstimat || []).map(e => e._id));
    }
    // ovrigaEstimat är en ny array varje render — jämför på id-strängen
    // i stället, annars nollställs valet vid varje omrendering.
    // eslint-disable-next-line
  }, [isOpen, (ovrigaEstimat || []).map(e => e._id).join(',')]);
  // Pending-läge när BK försökt skicka utan baspaket — visar inline-confirm
  const [pendingSend, setPendingSend] = useState(null); // null | { withMessage: boolean }

  // När modalen öppnas, fyll i placeholders
  useEffect(() => {
    if (isOpen) {
      setText(fillPlaceholders({
        firstName: customerFirstName,
        firstName2: beställare2FirstName,
        bkName: bkFirstName,
      }));
      setPendingSend(null);
    }
  }, [isOpen, customerFirstName, beställare2FirstName, bkFirstName]);

  const backdrop = useBackdropClose(onClose);

  if (!isOpen) return null;

  const performSend = (withMessage) => {
    const extra = (ovrigaEstimat || []).filter(e => valdaExtra.includes(e._id));
    onSend(withMessage ? text.trim() : '', versionComment, extra);
  };

  // Vid klick på Skicka — om inget baspaket finns, visa confirm-fråga först
  const requestSend = (withMessage) => {
    if (!hasBaspaket) {
      setPendingSend({ withMessage });
      return;
    }
    performSend(withMessage);
  };

  const handleSend = () => requestSend(true);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" {...backdrop}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Skicka estimat</h2>
          <p className="text-sm text-gray-600 mt-1">
            Redigera intro-meddelandet som visas högst upp i estimatet för kunden. Lämna tomt för att skicka utan intro-meddelande.
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Vem mailet går till. Skrivskyddad — utskicket skapar även konto och
              skickar inloggningsuppgifter till beställare 2, så att kryssa ur
              någon här skulle betyda mer än att hoppa över ett mail. */}
          <RecipientPicker
            recipients={mottagare}
            label="Skickas till"
            emptyText="Ingen mottagare med e-postadress hittades"
          />

          {/* Flera parallella estimat på samma lead — välj vilka som ska med.
              Kunden får ett enda mail oavsett hur många som skickas. */}
          {ovrigaEstimat && ovrigaEstimat.length > 0 && (
            <div className="mb-4 border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Skicka även</p>
              {ovrigaEstimat.map((e) => (
                <label key={e._id} className="flex items-center gap-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={valdaExtra.includes(e._id)}
                    onChange={() => setValdaExtra(prev => (
                      prev.includes(e._id) ? prev.filter(id => id !== e._id) : [...prev, e._id]
                    ))}
                    className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                  />
                  <span className="text-sm text-gray-700">
                    {e.variantName || 'Estimat'} {Number(e.version).toFixed(1)}
                    {e.status === 'draft' && <span className="text-xs text-orange-600 ml-1">(utkast)</span>}
                  </span>
                </label>
              ))}
              <p className="text-xs text-gray-500 mt-2">
                Kunden får ett mail och en notis, oavsett hur många estimat som skickas.
              </p>
            </div>
          )}

          <label className="block text-sm font-medium text-gray-700 mb-2">
            Intro-meddelande till kund
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={18}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
            placeholder="Skriv ett meddelande till kunden..."
          />
          <p className="text-xs text-gray-500 mt-2">
            Meddelandet visas som en sektion högst upp i estimatet och kan stängas av kunden. Det försvinner automatiskt efter att kunden skickat feedback första gången.
          </p>
        </div>

        {pendingSend && (
          <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1 text-sm text-yellow-900">
                <strong>Du har inte lagt till något baspaket.</strong>
                <p className="text-yellow-800 mt-1">Vill du skicka estimatet ändå?</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setPendingSend(null)}
                disabled={isSending}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Nej, lägg till baspaket
              </button>
              <button
                type="button"
                onClick={() => performSend(pendingSend.withMessage)}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg disabled:opacity-50"
              >
                {isSending ? 'Skickar...' : 'Ja, skicka ändå'}
              </button>
            </div>
          </div>
        )}

        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            disabled={isSending}
          >
            Avbryt
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => requestSend(false)}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isSending || !!pendingSend}
            >
              Skicka utan meddelande
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending || !!pendingSend}
              className="px-6 py-2 text-sm bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark disabled:opacity-50"
            >
              {isSending ? 'Skickar...' : 'Skicka estimat'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEstimatIntroModal;
